import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { CurrencyPicker } from "@/components/CurrencyPicker";
import { storage } from "@/lib/storage";
import { formatMoney, getCurrency } from "@/lib/currencies";
import { openRazorpayCheckout, razorpayApi } from "@/lib/razorpay";
import { toast } from "sonner";
import { Globe2, Zap, ShieldCheck, Check, Info } from "lucide-react";

export default function OnlinePay() {
  const navigate = useNavigate();
  const profile = storage.getProfile() || { id: "M000000", name: "Merchant" };
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState(storage.getCurrency());
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [note, setNote] = useState("");
  const [cfg, setCfg] = useState({ configured: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    razorpayApi.config().then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  const cur = getCurrency(currency);
  const valid = Number(amount) > 0;

  const pay = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const result = await openRazorpayCheckout({
        amount: Number(amount),
        currency,
        purpose: "customer_pay",
        merchantId: profile.id,
        merchantName: profile.name,
        customerName,
        customerEmail,
        customerContact,
        note,
      });
      // also save in offline local store so merchant history stays unified
      storage.addTransaction({
        direction: "received",
        amount: Number(amount),
        currency,
        method: "razorpay",
        note: `Online · ${note || ""}`.trim(),
        customer: customerName,
        razorpay_payment_id: result?.order?.razorpay_payment_id,
      });
      toast.success("Online payment received!");
      setDone(true);
      setTimeout(() => navigate("/merchant"), 1600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Payment failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-shell" data-testid="online-pay-page">
      <Header title="Online Payment" subtitle="Razorpay · Card · UPI · Netbanking" />

      {!cfg.configured && (
        <div className="card-glass rounded-xl p-4 mb-4 border border-amber-400/30 flex gap-3" data-testid="razorpay-not-configured">
          <Info size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-[#F7E6B4]/80 leading-relaxed">
            <strong className="text-amber-300">Razorpay keys needed.</strong> See the "Get Razorpay keys" guide
            below or go to <a href="/plans" className="underline">Plans</a> after setup. Once you paste
            your Test keys into <code className="text-[#E6B455]">backend/.env</code> and restart,
            Online Pay will work instantly.
          </div>
        </div>
      )}

      <div className="card-glass gold-glow rounded-2xl p-6 mb-5 fade-slide-up">
        <div className="flex items-center justify-between mb-4">
          <span className="stat-label">Amount to charge</span>
          <CurrencyPicker value={currency} onChange={setCurrency} compact />
        </div>
        <div className="flex items-center gap-3 mb-6">
          <span className="text-3xl font-bold text-[#E6B455]">{cur.symbol}</span>
          <input
            type="number"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="flex-1 bg-transparent text-5xl font-bold text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/20 min-w-0"
            data-testid="online-amount-input"
            autoFocus
          />
        </div>
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Customer name"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
            data-testid="online-name-input"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="email"
              placeholder="Email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              className="bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35 min-w-0"
              data-testid="online-email-input"
            />
            <input
              type="tel"
              placeholder="Phone"
              value={customerContact}
              onChange={(e) => setCustomerContact(e.target.value)}
              className="bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35 min-w-0"
              data-testid="online-phone-input"
            />
          </div>
          <input
            type="text"
            placeholder="Note / Order details (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full bg-[rgba(16,22,42,0.6)] border gold-border rounded-xl px-4 py-3 text-sm text-[#F7E6B4] outline-none placeholder:text-[#F7E6B4]/35"
            data-testid="online-note-input"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { icon: Globe2, label: "Secure" },
          { icon: Zap, label: "Instant" },
          { icon: ShieldCheck, label: "Verified" },
        ].map((f, i) => (
          <div key={i} className="stat-card text-center py-3">
            <f.icon size={16} className="text-[#E6B455] mx-auto mb-1" />
            <div className="text-[10px] uppercase tracking-wider text-[#F7E6B4]/60">{f.label}</div>
          </div>
        ))}
      </div>

      {done ? (
        <div className="btn-gold rounded-xl p-4 flex items-center justify-center gap-2" data-testid="online-done">
          <Check size={18} /> Online Payment Received!
        </div>
      ) : (
        <button
          onClick={pay}
          disabled={!valid || busy}
          className="btn-gold w-full py-3.5 rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="online-pay-btn"
        >
          {busy ? "Opening Razorpay…" : `Pay ${formatMoney(amount || 0, currency)}`}
        </button>
      )}

      <details className="mt-6 card-glass rounded-xl p-4" data-testid="razorpay-setup-guide">
        <summary className="cursor-pointer text-sm font-semibold text-[#E6B455]">
          How to get your Razorpay Test keys (free, 2 min)
        </summary>
        <ol className="text-xs text-[#F7E6B4]/75 mt-3 space-y-2 leading-relaxed list-decimal pl-5">
          <li>Go to <a className="underline text-[#E6B455]" href="https://dashboard.razorpay.com/signup" target="_blank" rel="noreferrer">dashboard.razorpay.com/signup</a> and sign up (free, no KYC for test mode)</li>
          <li>After login, switch to <strong>Test Mode</strong> toggle (top-right)</li>
          <li>Open <strong>Settings → API Keys → Generate Test Key</strong></li>
          <li>Copy both <strong>Key ID</strong> and <strong>Key Secret</strong></li>
          <li>Open <code className="text-[#E6B455]">backend/.env</code> and paste values into <code className="text-[#E6B455]">RAZORPAY_KEY_ID</code> and <code className="text-[#E6B455]">RAZORPAY_KEY_SECRET</code></li>
          <li>Restart the backend. Done — Online Pay now works end-to-end</li>
        </ol>
        <p className="text-[11px] text-[#F7E6B4]/50 mt-3">
          Test cards: <code className="text-[#E6B455]">4111 1111 1111 1111</code>, any future expiry, any CVV.
        </p>
      </details>
    </div>
  );
}
